import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { queueProgressUpdate } from '../pwa/offlineQueue';
import { SkeletonCard } from '../components/common/SkeletonCard';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Bookmark, BookmarkCheck,
  PanelRightOpen, PanelRightClose, Layers, Maximize2,
} from 'lucide-react';
import { flattenSlides } from './player/flattenSlides';
import { useFullscreen } from './player/useFullscreen';
import { useDeckUsageTracker } from './player/useDeckUsageTracker';
import { SlideStage } from './player/SlideStage';
import { PlayerFooter } from './player/PlayerFooter';
import { PlayerSidePanel } from './player/PlayerSidePanel';
import { PresentationOverlay } from './player/PresentationOverlay';

export default function ModulePlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { get, post } = useApi();
  const { user } = useAuth();
  const [mod, setMod] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launchingLive, setLaunchingLive] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTab, setPanelTab] = useState('content');
  const [noteText, setNoteText] = useState('');

  // Anyone trainer+ can present a module (trainers, editors, org admins, …)
  const isHost = user?.role && ['trainer', 'editor', 'editor_trainer', 'org_admin', 'admin', 'platform_admin', 'super_admin'].includes(user.role);

  const [allSlides, setAllSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const slideTimerRef = useRef(null);
  const viewedRef = useRef(new Set());

  const { presenting, targetRef: presentRef, enter: enterPresentation, exit: exitPresentation } = useFullscreen();

  // Initial load
  useEffect(() => {
    async function load() {
      try {
        const [moduleData, enrollments] = await Promise.all([
          get(`/modules/${id}`),
          get('/enrollments/me').catch(() => []),
        ]);
        setMod(moduleData);
        const myEnroll = (Array.isArray(enrollments) ? enrollments : []).find(e => e.module_id === id);
        setEnrollment(myEnroll || null);

        const slides = flattenSlides(moduleData);
        setAllSlides(slides);

        // Query-param-driven jump points (presentation mode controls):
        //   ?from_section=<id>          → start at first slide of that section
        //   ?resume_after_section=<id>  → start at first slide AFTER that section
        const fromSection = searchParams.get('from_section');
        const resumeAfter = searchParams.get('resume_after_section');
        let qpIdx = -1;
        if (resumeAfter) {
          // index of last item belonging to that section, +1
          const lastIdx = slides.map((sl, i) => ({ sl, i }))
            .filter(({ sl }) => sl.section?.section_id === resumeAfter)
            .map(({ i }) => i);
          if (lastIdx.length > 0) qpIdx = Math.min(slides.length - 1, lastIdx[lastIdx.length - 1] + 1);
        } else if (fromSection) {
          qpIdx = slides.findIndex(sl => sl.section?.section_id === fromSection);
        }
        if (qpIdx >= 0) {
          setCurrentIndex(qpIdx);
          return;
        }

        if (myEnroll) {
          try {
            const prog = await get(`/progress/enrollment/${myEnroll.enrollment_id}`);
            setProgress(prog);
            if (prog.current_slide_id) {
              const idx = slides.findIndex(s => s.type === 'slide' && s.slide?.slide_id === prog.current_slide_id);
              if (idx >= 0) setCurrentIndex(idx);
            }
          } catch (err) { console.error('Progress load failed:', err); }
        }
      } catch (err) { console.error('Module player load failed:', err); } finally { setLoading(false); }
    }
    load();
  }, [get, id, searchParams]);

  const current = allSlides[currentIndex];
  const totalSlides = allSlides.length;

  // Usage telemetry — records this presentation in Activity Logs → Usage when
  // the trainer is in fullscreen/projector mode (best-effort, non-blocking).
  useDeckUsageTracker({
    presenting,
    moduleId: id,
    moduleTitle: mod?.title,
    currentIndex,
    totalSlides,
  });

  // Slide-view tracking (debounced 2 s)
  const trackSlideView = useCallback(async (slideItem) => {
    if (!enrollment || !slideItem || slideItem.type !== 'slide') return;
    const slideId = slideItem.slide.slide_id;
    if (viewedRef.current.has(slideId)) return;
    viewedRef.current.add(slideId);
    const url = `/progress/enrollment/${enrollment.enrollment_id}/slide-viewed`;
    const payload = { slide_id: slideId, time_spent_seconds: 5 };
    if (navigator.onLine) {
      try { await post(url, payload); } catch (err) { console.error('Slide view tracking failed:', err); }
    } else {
      // Offline → queue for SW background sync. Auth rides on httpOnly cookies,
      // which the service worker fetch will carry automatically on replay.
      await queueProgressUpdate(`/api/v1${url}`, payload);
    }
  }, [enrollment, post]);

  useEffect(() => {
    if (!current) return;
    clearTimeout(slideTimerRef.current);
    slideTimerRef.current = setTimeout(() => trackSlideView(current), 2000);
    return () => clearTimeout(slideTimerRef.current);
  }, [currentIndex, current, trackSlideView]);

  /** Launch a Live session bound to the section's quiz, then deep-link into the
   *  host page with a return-to URL so the trainer can resume the presentation
   *  at the slide AFTER the quiz section once the session ends. */
  const launchLiveForQuizSection = useCallback(async (quizItem) => {
    if (!isHost) {
      toast.error('Only trainers can start live quizzes. Wait for your trainer to launch it.');
      return;
    }
    if (launchingLive) return;
    setLaunchingLive(true);
    try {
      const resumeUrl = `/modules/${id}/learn?resume_after_section=${quizItem.section.section_id}`;
      const res = await post('/live/sessions', {
        quiz_id: quizItem.quiz_id,
        mode: 'section_run',
        target_module_id: id,
        target_section_id: quizItem.section.section_id,
        require_participant_email: true,
      });
      toast.success(`Live session ready · code ${res.ref_code}`);
      navigate(`/editor/live?session=${res.session_id}&return_to=${encodeURIComponent(resumeUrl)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start live session');
    } finally {
      setLaunchingLive(false);
    }
  }, [isHost, launchingLive, id, post, navigate]);

  const goToPrev = () => setCurrentIndex(i => Math.max(0, i - 1));
  const goToNext = () => {
    if (currentIndex >= totalSlides - 1) return;
    const next = allSlides[currentIndex + 1];
    if (next?.type === 'quiz') {
      launchLiveForQuizSection(next);
      return;
    }
    setCurrentIndex(i => Math.min(totalSlides - 1, i + 1));
  };

  /** Start-quiz CTA on the quiz intro card uses the SAME launch flow. */
  const startCurrentQuizLive = () => {
    if (current?.type !== 'quiz') return;
    launchLiveForQuizSection(current);
  };

  // Keyboard navigation + present shortcut
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goToNext(); }
      else if ((e.key === 'p' || e.key === 'P') && !e.metaKey && !e.ctrlKey) {
        const tag = (e.target?.tagName || '').toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        if (!presenting) enterPresentation();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const toggleBookmark = async () => {
    if (!enrollment || !current || current.type !== 'slide') return;
    const slideId = current.slide.slide_id;
    const isBookmarked = (progress?.bookmarks || []).some(b => b.slide_id === slideId);
    try {
      if (isBookmarked) {
        await post(`/progress/enrollment/${enrollment.enrollment_id}/bookmark/${slideId}`, null, { method: 'DELETE' });
      } else {
        await post(`/progress/enrollment/${enrollment.enrollment_id}/bookmark`, { slide_id: slideId, section_id: current.section.section_id });
      }
      const prog = await get(`/progress/enrollment/${enrollment.enrollment_id}`);
      setProgress(prog);
    } catch (err) { console.error('Bookmark toggle failed:', err); }
  };

  const saveNote = async () => {
    if (!enrollment || !current || current.type !== 'slide' || !noteText.trim()) return;
    try {
      await post(`/progress/enrollment/${enrollment.enrollment_id}/note`, {
        slide_id: current.slide.slide_id, section_id: current.section.section_id, content: noteText,
      });
      const prog = await get(`/progress/enrollment/${enrollment.enrollment_id}`);
      setProgress(prog);
    } catch (err) { console.error('Note save failed:', err); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><SkeletonCard className="w-[600px] h-[400px]" /></div>;
  }
  if (!mod || totalSlides === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center space-y-4">
          <Layers className="w-16 h-16 mx-auto text-muted-foreground/40" />
          <h2 className="text-xl font-semibold">No content available</h2>
          <Button asChild variant="outline"><Link to={`/modules/${id}`}><ArrowLeft className="w-4 h-4 mr-2" />Back to Module</Link></Button>
        </div>
      </div>
    );
  }

  const isBookmarked = current?.type === 'slide' && (progress?.bookmarks || []).some(b => b.slide_id === current.slide?.slide_id);
  const sectionIdx = mod.sections?.findIndex(s => current?.section?.section_id === s.section_id) ?? 0;

  return (
    <div className="flex flex-col h-screen bg-slate-950 dark text-slate-100 relative overflow-hidden" data-testid="module-player">
      {/* Theater ambience: subtle slide-tinted vignette + dot grid */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-15 bg-[var(--lumyn-content)]" />
        <div className="absolute bottom-0 -right-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-12 bg-[var(--lumyn-brand)]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />
      </div>

      <div className="relative z-10 flex flex-col h-screen">
      <div className="h-14 border-b border-white/10 flex items-center justify-between px-4 shrink-0 bg-slate-950/60 backdrop-blur-xl">
        <Link to={`/modules/${id}`} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition" data-testid="back-to-module">
          <ArrowLeft className="w-4 h-4" /> Back to Module
        </Link>
        <div className="text-center">
          <p className="text-sm font-display font-semibold truncate max-w-[420px] text-white">{current?.sectionTitle}</p>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
            {current?.type === 'slide' ? `Slide ${currentIndex + 1} of ${totalSlides}` : 'Quiz Section'}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {current?.type === 'slide' && (
            <>
              <button onClick={toggleBookmark} className="p-2 rounded-lg hover:bg-white/10 transition" data-testid="bookmark-toggle" title="Bookmark this slide">
                {isBookmarked ? <BookmarkCheck className="w-5 h-5 text-amber-300" /> : <Bookmark className="w-5 h-5 text-slate-400" />}
              </button>
              <button onClick={enterPresentation} className="p-2 rounded-lg hover:bg-white/10 transition" title="Present (P)" data-testid="present-btn">
                <Maximize2 className="w-5 h-5 text-slate-300" />
              </button>
            </>
          )}
          <button onClick={() => setPanelOpen(p => !p)} className="p-2 rounded-lg hover:bg-white/10 transition" data-testid="panel-toggle">
            {panelOpen ? <PanelRightClose className="w-5 h-5 text-slate-300" /> : <PanelRightOpen className="w-5 h-5 text-slate-300" />}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-6 overflow-auto">
            <SlideStage
              current={current}
              currentIndex={currentIndex}
              onStartLiveQuiz={startCurrentQuizLive}
              launchingLive={launchingLive}
              canHost={isHost}
            />
          </div>
          <PlayerFooter
            currentIndex={currentIndex}
            totalSlides={totalSlides}
            sectionIdx={sectionIdx}
            totalSections={mod.sections?.length || 0}
            onPrev={goToPrev}
            onNext={goToNext}
            allSlides={allSlides}
            launchingLive={launchingLive}
          />
        </div>

        {panelOpen && (
          <PlayerSidePanel
            panelTab={panelTab}
            onTabChange={setPanelTab}
            current={current}
            progress={progress}
            allSlides={allSlides}
            onJumpTo={setCurrentIndex}
            noteText={noteText}
            onNoteChange={(e) => setNoteText(e.target.value)}
            onSaveNote={saveNote}
          />
        )}
      </div>

      {presenting && (
        <PresentationOverlay
          ref={presentRef}
          current={current}
          currentIndex={currentIndex}
          totalSlides={totalSlides}
          onExit={exitPresentation}
        />
      )}
      </div>
    </div>
  );
}
